import { useState, useEffect } from "react";
import api from "../../../../../Axios";
import { toast } from "react-toastify";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  FormHelperText,
  Autocomplete
} from "@mui/material";
import useConfirm from "../../../../../hooks/useConfirm";

export default function Step4Skills({ programId, initialData, setMasterRender, next, prev }) {

  const [loading, setLoading] = useState(false);
  const [skillsList, setSkillsList] = useState([]);

  const [editingSkillId, setEditingSkillId] = useState(null);

  const [skill, setSkill] = useState({
    skill_id: "",
    target_level: "L1",
    rating_gain: 1
  });

  const [errors, setErrors] = useState({});

  const [skills, setSkills] = useState([]);

  useEffect(() => {
    if (initialData && Array.isArray(initialData.skill_outcomes)) {
      setSkills(initialData.skill_outcomes);
    }
  }, [initialData]);

  const fetchSkills = async () => {
    try {
      const res = await api.get("/lnd/get-skill-dropdown");
      setSkillsList(res.data.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load skills");
    }
  };

  useEffect(() => {
    fetchSkills();
  }, []);

  const validateForm = () => {
    let newErrors = {};
    if (!skill.skill_id) newErrors.skill_id = "Skill is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addSkill = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        program_id: programId,
        skill_id: Number(skill.skill_id),
        target_level: skill.target_level,
        rating_gain: Number(skill.rating_gain)
      };

      const res = await api.post("/lnd/skill", payload);
      const savedSkill = res.data?.data?.skill || payload;

      setSkills(prev => [...prev, savedSkill]);
      setMasterRender(prev => !prev);

      resetForm();
      toast.success("Skill added successfully");

    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add skill");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     UPDATE SKILL
  ========================== */
  const updateSkill = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        program_id: programId,
        skill_id: Number(skill.skill_id),
        target_level: skill.target_level,
        rating_gain: Number(skill.rating_gain)
      };

      await api.put(`/lnd/update-skill/${editingSkillId}`, payload);

      setSkills(prev =>
        prev.map(s =>
          s.id === editingSkillId ? { ...s, ...payload } : s
        )
      );

      toast.success("Skill updated successfully");
      setMasterRender((prev) => !prev);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update skill");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     EDIT CLICK
  ========================== */
  const handleEdit = (s) => {
    setSkill({
      skill_id: s.skill_id || "",
      target_level: s.target_level || "L1",
      rating_gain: s.rating_gain || 1
    });

    setEditingSkillId(s.id);
  };

  /* =========================
   DELETE SKILL
========================= */
  const { confirm } = useConfirm();
  const deleteSkill = async (skillId) => {

    try {
      const isConfirmed = await confirm({
        title: "Confirm Skill Deletion",
        content: `Are you sure you want to delete this skill?`,
        confirmText: "Yes, Delete",
        cancelText: "Cancel"
      });
      if (!isConfirmed) return;

      await api.delete(`/lnd/delete-skill-outcome/${skillId}`);

      // remove from UI
      setSkills(prev => prev.filter(s => s.id !== skillId));

      setMasterRender(prev => !prev);

      toast.success("Skill deleted successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete skill");
    }
  };

  const resetForm = () => {
    setSkill({
      skill_id: "",
      target_level: "L1",
      rating_gain: 1
    });
    setErrors({});
    setEditingSkillId(null);
  };

  return (
    <div className="container-fluid">
      <div className="row">

        {/* LEFT SIDE FORM */}
        <div className="col-md-7">

          <div className="card shadow-sm border-0 mb-4 p-4 rounded-3">
            <Typography variant="h6" className="fw-bold" sx={{ mb: 4 }}>
              {editingSkillId ? "Edit Skill" : "Add Skill"}
            </Typography>

            {/* SKILL DROPDOWN */}

            <div className="mb-3">
              <Autocomplete
                options={skillsList || []}
                getOptionLabel={(option) => option._Stitle || ""}

                // 🔥 map id → object (IMPORTANT for edit mode)
                value={
                  skillsList.find((s) => s._Sid === skill.skill_id) || null
                }

                onChange={(e, newValue) => {
                  setSkill({
                    ...skill,
                    skill_id: newValue?._Sid || "",
                  });

                  if (errors.skill_id) {
                    setErrors({
                      ...errors,
                      skill_id: "",
                    });
                  }
                }}

                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Choose Skill *"
                    size="small"
                    error={!!errors.skill_id}
                    helperText={errors.skill_id}
                  />
                )}
              />
            </div>

            {/* TARGET LEVEL */}
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Skill Level *</InputLabel>
              <Select
                value={skill.target_level}
                label="Skill Level *"
                onChange={(e) =>
                  setSkill({ ...skill, target_level: e.target.value })
                }
              >
                <MenuItem value="L1">L1</MenuItem>
                <MenuItem value="L2">L2</MenuItem>
                <MenuItem value="L3">L3</MenuItem>
                <MenuItem value="L4">L4</MenuItem>
              </Select>
            </FormControl>

            {/* RATING GAIN (Dropdown 0-10) */}
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Proficiency Level *</InputLabel>
              <Select
                value={skill.rating_gain}
                label="Proficiency Level *"
                onChange={(e) =>
                  setSkill({
                    ...skill,
                    rating_gain: Number(e.target.value)
                  })
                }
              >
                {[...Array(11)].map((_, i) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 4
              }}
            >

              <div className="d-flex justify-content-end w-100 gap-3">
                {editingSkillId && (
                  <button
                    className="btn btn-secondary px-4 fw-medium"
                    onClick={resetForm}
                  >
                    Cancel Edit
                  </button>
                )}
                <button
                  className="btn btn-success px-4 fw-medium"
                  onClick={editingSkillId ? updateSkill : addSkill}
                  disabled={loading}
                >
                  {loading
                    ? "Saving..."
                    : editingSkillId
                      ? "Update Skill"
                      : "Add Skill"}
                </button>
              </div>
            </Box>
          </div>

          <div className="d-flex justify-content-between mt-4 px-2">
            <button className="btn btn-secondary px-4 fw-medium" onClick={prev}>
              Previous
            </button>

            <button
              className="btn btn-success px-4 fw-medium"
              onClick={next}
              disabled={skills.length === 0}
            >
              Save & Continue
            </button>
          </div>

        </div>

        {/* RIGHT SIDE LIST */}
        <div className="col-md-5">
          <h5 className="mb-3">Added Skills</h5>

          {skills.length === 0 && (
            <p className="text-muted">No skills added yet</p>
          )}

          <ul className="list-group">
            {skills.map((s) => (
              <li
                key={s.id}
                className="list-group-item d-flex justify-content-between align-items-start"
              >
                <div>
                  <strong>
                    {s?.skill?._Stitle || "Skill ID: " + s.skill_id}
                  </strong>
                  <p className="mb-0">
                    Skill Level: {s.target_level} | Proficiency Level: {s.rating_gain}
                  </p>
                </div>

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleEdit(s)}
                  >
                    Edit
                  </button>

                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => deleteSkill(s.id)}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

      </div>
    </div>
  );
}